import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const DevelopersPage = () => {
    const [activeTab, setActiveTab] = useState('quickstart');
    const [copiedCode, setCopiedCode] = useState('');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const copyToClipboard = (code, id) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(id);
        setTimeout(() => setCopiedCode(''), 2000);
    };

    const codeExamples = {
        quickstart: `// Initialize EADC Client
import { EADCClient } from '@angalifu/eadc-sdk';

const client = new EADCClient({
  apiKey: 'your-api-key',
  region: 'global'
});

// Simple wave computation
const result = await client.compute({
  type: 'wave-interference',
  sources: [
    { x: 0, y: 0, frequency: 440 },
    { x: 100, y: 0, frequency: 880 }
  ]
});`,

        fieldComputation: `// Electromagnetic field computation
const fieldCompute = client.createFieldComputation();

// Define field parameters
fieldCompute.setField({
  type: 'electromagnetic',
  dimensions: 3,
  resolution: 1024
});

// Add sources
fieldCompute.addSource({
  position: [0, 0, 0],
  amplitude: 1.0,
  phase: 0
});

// Execute computation
const result = await fieldCompute.execute();`,

        continuousAI: `// Continuous AI inference
const aiModel = client.loadModel('continuous-transformer');

// Stream continuous data
const stream = aiModel.createStream({
  inputDimensions: 512,
  outputDimensions: 256,
  mode: 'continuous'
});

// Process in real-time
stream.on('data', (output) => {
  console.log('Continuous output:', output);
});

stream.write(continuousData);`
    };

    return (
    <>
            <style>{`
        .dev-hero {
          padding: 10rem 2rem 4rem;
          background: linear-gradient(135deg, var(--gray-900), var(--black));
          color: var(--white);
          position: relative;
          overflow: hidden;
        }

        .dev-hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            repeating-linear-gradient(
              0deg,
              transparent,
              transparent 2px,
              rgba(59, 130, 246, 0.1) 2px,
              rgba(59, 130, 246, 0.1) 4px
            );
          pointer-events: none;
        }

        .dev-hero-content {
          max-width: 1280px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .dev-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: rgba(59, 130, 246, 0.2);
          border: 1px solid rgba(59, 130, 246, 0.5);
          border-radius: 50px;
          font-size: 0.875rem;
          font-weight: 600;
          margin-bottom: 2rem;
        }

        .dev-hero-title {
          font-size: clamp(3rem, 6vw, 4.5rem);
          font-weight: 800;
          margin-bottom: 1.5rem;
          letter-spacing: -0.05em;
        }

        .dev-hero-description {
          font-size: 1.375rem;
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.6;
          max-width: 800px;
          margin-bottom: 3rem;
        }

        .dev-hero-actions {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .dev-hero-cta {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem 2rem;
          background: var(--blue-600);
          color: var(--white);
          border-radius: var(--radius);
          text-decoration: none;
          font-weight: 600;
          transition: all var(--transition-base);
        }

        .dev-hero-cta:hover {
          background: var(--blue-500);
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(59, 130, 246, 0.3);
        }

        .dev-content {
          padding: 6rem 2rem;
          max-width: 1280px;
          margin: 0 auto;
        }

        .dev-tabs {
          display: flex;
          gap: 1rem;
          margin-bottom: 3rem;
          border-bottom: 1px solid var(--gray-200);
          overflow-x: auto;
        }

        .dev-tab {
          padding: 1rem 1.5rem;
          background: none;
          border: none;
          font-size: 1rem;
          font-weight: 600;
          color: var(--gray-600);
          cursor: pointer;
          position: relative;
          transition: all var(--transition-fast);
          white-space: nowrap;
        }

        .dev-tab:hover {
          color: var(--gray-900);
        }

        .dev-tab.active {
          color: var(--blue-600);
        }

        .dev-tab::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 0;
          width: 100%;
          height: 3px;
          background: var(--blue-600);
          transform: scaleX(0);
          transition: transform var(--transition-base);
        }

        .dev-tab.active::after {
          transform: scaleX(1);
        }

        .code-section {
          margin-bottom: 3rem;
        }

        .code-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .code-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--gray-900);
        }

        .code-block {
          position: relative;
          background: var(--gray-900);
          border-radius: var(--radius-lg);
          padding: 2rem;
          overflow-x: auto;
        }

        .code-content {
          color: var(--gray-100);
          font-family: 'Consolas', 'Monaco', monospace;
          font-size: 0.875rem;
          line-height: 1.6;
          white-space: pre;
        }

        .copy-button {
          position: absolute;
          top: 1rem;
          right: 1rem;
          padding: 0.5rem 1rem;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: var(--radius);
          color: var(--white);
          font-size: 0.875rem;
          cursor: pointer;
          transition: all var(--transition-fast);
        }

        .copy-button:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        .copy-button.copied {
          background: var(--green-600);
          border-color: var(--green-600);
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          margin-top: 4rem;
        }

        .feature-card {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: var(--radius-lg);
          padding: 2rem;
          transition: all var(--transition-base);
        }

        .feature-card:hover {
          box-shadow: var(--shadow-md);
          transform: translateY(-2px);
          border-color: var(--blue-200);
        }

        .feature-icon {
          width: 48px;
          height: 48px;
          background: var(--blue-100);
          border-radius: var(--radius);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1rem;
          color: var(--blue-600);
        }

        .feature-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 0.5rem;
        }

        .feature-description {
          color: var(--gray-600);
          line-height: 1.6;
        }

        .resources-section {
          padding: 6rem 2rem;
          background: var(--gray-50);
        }

        .resources-container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .resources-title {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--gray-900);
          text-align: center;
          margin-bottom: 3rem;
        }

        .resources-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
        }

        .resource-link {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: var(--radius);
          padding: 1.5rem;
          text-decoration: none;
          color: var(--gray-900);
          transition: all var(--transition-base);
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .resource-link:hover {
          box-shadow: var(--shadow-md);
          transform: translateY(-2px);
          border-color: var(--blue-200);
        }

        .resource-icon {
          width: 40px;
          height: 40px;
          background: var(--gray-100);
          border-radius: var(--radius);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .resource-text {
          flex: 1;
        }

        .resource-title {
          font-weight: 600;
          margin-bottom: 0.25rem;
        }

        .resource-description {
          font-size: 0.875rem;
          color: var(--gray-600);
        }

        @media (max-width: 768px) {
          .dev-hero {
            padding: 8rem 1rem 3rem;
          }

          .dev-hero-actions {
            flex-direction: column;
          }

          .dev-tabs {
            gap: 0.5rem;
          }

          .dev-tab {
            padding: 0.75rem 1rem;
            font-size: 0.875rem;
          }

          .code-block {
            padding: 1rem;
          }

          .copy-button {
            top: 0.5rem;
            right: 0.5rem;
            padding: 0.25rem 0.5rem;
            font-size: 0.75rem;
          }
        }
      `}</style>

      <section className="dev-hero">
        <div className="dev-hero-content">
          <div className="dev-hero-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
            Developer Portal
          </div>
          <h1 className="dev-hero-title">
            Build with Electromagnetic Computing
          </h1>
          <p className="dev-hero-description">
            Access the world's first electromagnetic field computation platform. Our SDK provides seamless integration with existing codebases while unlocking unprecedented computational capabilities.
          </p>
          <div className="dev-hero-actions">
            <a href="#quickstart" className="dev-hero-cta">
              Get Started
              <span>→</span>
            </a>
            <Link to="/developers/docs" className="dev-hero-cta" style={{ background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(10px)' }}>
              View Documentation
            </Link>
          </div>
        </div>
      </section>

      <section className="dev-content">
        <div className="dev-tabs">
          <button
            className={`dev-tab ${activeTab === 'quickstart' ? 'active' : ''}`}
            onClick={() => setActiveTab('quickstart')}
          >
            Quick Start
          </button>
          <button
            className={`dev-tab ${activeTab === 'field' ? 'active' : ''}`}
            onClick={() => setActiveTab('field')}
          >
            Field Computation
          </button>
          <button
            className={`dev-tab ${activeTab === 'ai' ? 'active' : ''}`}
            onClick={() => setActiveTab('ai')}
          >
            Continuous AI
          </button>
        </div>

        <div className="code-section">
          <div className="code-header">
            <h3 className="code-title">
              {activeTab === 'quickstart' && 'Quick Start Guide'}
              {activeTab === 'field' && 'Field Computation Example'}
              {activeTab === 'ai' && 'Continuous AI Integration'}
            </h3>
          </div>
          <div className="code-block">
            <pre className="code-content">{codeExamples[activeTab === 'field' ? 'fieldComputation' : activeTab === 'ai' ? 'continuousAI' : 'quickstart']}</pre>
            <button
              className={`copy-button ${copiedCode === activeTab ? 'copied' : ''}`}
              onClick={() => copyToClipboard(codeExamples[activeTab === 'field' ? 'fieldComputation' : activeTab === 'ai' ? 'continuousAI' : 'quickstart'], activeTab)}
            >
              {copiedCode === activeTab ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
            </div>
            <h3 className="feature-title">Lightning Fast</h3>
            <p className="feature-description">
              Computation at the speed of light with nanosecond latency for real-time applications
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
            </div>
            <h3 className="feature-title">Global Scale</h3>
            <p className="feature-description">
              Planetary-scale distributed computation with perfect coherence across continents
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </div>
            <h3 className="feature-title">Cost Effective</h3>
            <p className="feature-description">
              1000x more energy efficient than traditional computing with usage-based pricing
            </p>
          </div>
        </div>
      </section>

      <section className="resources-section">
        <div className="resources-container">
          <h2 className="resources-title">Developer Resources</h2>
          <div className="resources-grid">
            <Link to="/developers/docs" className="resource-link">
              <div className="resource-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </div>
              <div className="resource-text">
                <div className="resource-title">Documentation</div>
                <div className="resource-description">Complete API reference and guides</div>
              </div>
            </Link>

            <Link to="/developers/tutorials" className="resource-link">
              <div className="resource-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="12 2 2 7 12 12 22 7 12 2" />
                  <polyline points="2 17 12 22 22 17" />
                  <polyline points="2 12 12 17 22 12" />
                </svg>
              </div>
              <div className="resource-text">
                <div className="resource-title">Tutorials</div>
                <div className="resource-description">Step-by-step learning paths</div>
              </div>
            </Link>

            <Link to="/developers/sdk" className="resource-link">
              <div className="resource-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                  <line x1="12" y1="22.08" x2="12" y2="12" />
                </svg>
              </div>
              <div className="resource-text">
                <div className="resource-title">SDK Downloads</div>
                <div className="resource-description">Libraries for all platforms</div>
              </div>
            </Link>

            <Link to="/developers/community" className="resource-link">
              <div className="resource-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="resource-text">
                <div className="resource-title">Community</div>
                <div className="resource-description">Connect with other developers</div>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default DevelopersPage;
